// Shared layout for /login, /signup, /forgot-password, /reset-password.
// Two-column premium split: left form column, right marketing/brand column.
// Uses existing ChainFlow tokens (violet gradient, cf-eyebrow, cf-h-page).

import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Building2, ShieldCheck, Rocket } from "lucide-react";
import { ChainFlowLogo } from "../../components/brand/ChainFlowLogo";

const HIGHLIGHTS = [
  { icon: Building2,   label: "Operating system",      copy: "One workspace for properties, deals, contacts and progression." },
  { icon: ShieldCheck, label: "Compliance-ready",      copy: "AML, EPC and lease pack tracking baked in by default." },
  { icon: Sparkles,    label: "Blocker intelligence",  copy: "Surface what's blocked, by whom, for how long — and act in one tap." },
  { icon: Rocket,      label: "Launch readiness",      copy: "Premium onboarding for vendors, buyers and brokers." },
];

export const AuthLayout = ({ eyebrow, title, subtitle, children, footer, testid }) => (
  <div className="min-h-screen flex" style={{ background: "var(--cf-app-bg)" }} data-testid={testid || "auth-layout"}>
    {/* Form column */}
    <div className="flex-1 flex flex-col px-6 sm:px-10 py-8">
      <Link to="/" className="inline-flex items-center self-start" data-testid="auth-brand">
        <ChainFlowLogo variant="full" size={30} />
      </Link>

      <div className="flex-1 flex items-center">
        <div className="w-full max-w-[460px] mx-auto">
          {eyebrow && <p className="cf-eyebrow cf-eyebrow-emerald mb-2">{eyebrow}</p>}
          <h1 className="cf-h-page mb-2.5" data-testid="auth-title">{title}</h1>
          {subtitle && <p className="text-[13.5px] text-slate-500 mb-7 leading-relaxed">{subtitle}</p>}
          {children}
          {footer && <div className="mt-6 text-[12.5px] text-slate-500">{footer}</div>}
        </div>
      </div>

      <p className="text-[11px] text-slate-400 self-start">© {new Date().getFullYear()} ChainFlow · Premium proptech operating system</p>
    </div>

    {/* Brand column */}
    <aside className="hidden lg:flex flex-col w-[460px] xl:w-[540px] shrink-0 relative overflow-hidden border-l border-slate-200">
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800" />
      <div className="absolute inset-0 opacity-[0.18]" style={{ backgroundImage: "radial-gradient(at 20% 20%, white 0, transparent 40%), radial-gradient(at 80% 80%, white 0, transparent 40%)" }} />
      <div className="relative flex-1 flex flex-col justify-center px-12 py-14 text-white">
        <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-indigo-200">A clearer chain</p>
        <h2 className="font-display font-bold text-[34px] leading-[1.1] mt-3">The transaction operating system for modern property professionals.</h2>
        <p className="text-[14px] text-indigo-100 mt-4 max-w-[400px]">Run sales progression, vendor onboarding, blocker intelligence and client portals from a single, beautifully calm workspace.</p>
        <ul className="mt-10 space-y-5 max-w-[420px]">
          {HIGHLIGHTS.map((h) => (
            <li key={h.label} className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0 ring-1 ring-white/10">
                <h.icon size={15} className="text-white" />
              </div>
              <div>
                <p className="text-[13px] font-bold">{h.label}</p>
                <p className="text-[12px] text-indigo-100/90 leading-snug mt-0.5">{h.copy}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  </div>
);

export const Field = ({ label, error, children }) => (
  <label className="block mb-4">
    <span className="text-[11.5px] font-semibold text-slate-700 uppercase tracking-[0.12em]">{label}</span>
    <div className="mt-1.5">{children}</div>
    {error && <p className="text-[11.5px] text-rose-600 mt-1.5" data-testid="field-error">{error}</p>}
  </label>
);

export const inputCls =
  "w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-[13.5px] text-slate-900 placeholder-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all";
